/**
 * Item 266 — switch an add-on detection model (Train Detection) on or off for every camera
 * in a group.
 *
 * Modelled on GroupDefaultViewDialog (item 238): the server decides which cameras the user
 * may change (the camera's owner or a superuser), so the choice is first sent as a dry run
 * and the dialog shows its counts; Apply repeats the same call for real. A one-time action on
 * the cameras that are in the group now — a camera added later is not changed.
 */
import * as Dialog from '@radix-ui/react-dialog'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cameraGroupsApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { toast } from '@/lib/toast'
import type { CameraGroup } from '@/types'

type Action = 'on' | 'off'
const ACTIONS: Action[] = ['on', 'off']

interface Props {
  group: CameraGroup
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function GroupDetectionDialog({ group, open, onOpenChange }: Props) {
  const { t } = useTranslation('groups')
  const { t: tc } = useTranslation('common')
  const qc = useQueryClient()
  const [modelId, setModelId] = useState<number | null>(null)
  const [action, setAction] = useState<Action>('on')

  const models = useQuery({
    queryKey: queryKeys.cameraGroups.detectionModels(group.id),
    queryFn: ({ signal }) => cameraGroupsApi.getDetectionModels(group.id, signal),
    enabled: open,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })
  const list = models.data?.models ?? []
  const model = list.find((m) => m.id === modelId) ?? list[0]
  const subscribed = action === 'on'

  const preview = useQuery({
    queryKey: queryKeys.cameraGroups.detectionModelPreview(group.id, model?.id ?? 0, subscribed),
    queryFn: ({ signal }) => cameraGroupsApi.setDetectionModel(group.id, model!.id, subscribed, true, signal),
    enabled: open && !!model,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })

  const apply = useMutation({
    mutationFn: () => cameraGroupsApi.setDetectionModel(group.id, model!.id, subscribed, false),
    onSuccess: (r) => {
      toast.success(r.skipped.length
        ? t('detection.appliedSkipped', { count: r.updated.length, skipped: r.skipped.length, model: r.ai_model.name })
        : t('detection.applied', { count: r.updated.length, model: r.ai_model.name }))
      qc.invalidateQueries({ queryKey: ['cameras'] })
      qc.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
      // The camera's own Detection screen keeps its list under this key.
      qc.invalidateQueries({ queryKey: ['camera-ai-model-configs'] })
      onOpenChange(false)
    },
    onError: () => toast.error(t('detection.failed')),
  })

  const counts = preview.data
  const canApply = !!model && !!counts && !preview.isFetching && counts.updated.length > 0 && !apply.isPending

  return (
    <Dialog.Root open={open} onOpenChange={apply.isPending ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Dialog.Title className="text-lg font-semibold">{t('detection.title', { name: group.name })}</Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">{t('detection.description')}</Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button type="button" aria-label={tc('actions.close')} disabled={apply.isPending}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {models.isError ? (
            <p className="mt-4 text-sm text-destructive">{t('detection.loadFailed')}</p>
          ) : !models.data ? (
            <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />{t('detection.loading')}
            </p>
          ) : !model ? (
            <p className="mt-4 text-sm text-muted-foreground">{t('detection.noModels')}</p>
          ) : (
            <>
              <div className="mt-4 space-y-1.5">
                <label className="text-sm font-medium" htmlFor="group-detection-model">{t('detection.modelLabel')}</label>
                <Select value={String(model.id)} onValueChange={(v) => setModelId(Number(v))} disabled={apply.isPending}>
                  <SelectTrigger id="group-detection-model">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {list.map((m) => (
                      <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {model.description && <p className="text-xs text-muted-foreground">{model.description}</p>}
                <p className="text-xs text-muted-foreground">
                  {t('detection.status', { on: model.subscribed_count, total: models.data.group.member_count })}
                </p>
              </div>

              <div className="mt-4 space-y-1.5">
                <label className="text-sm font-medium" htmlFor="group-detection-action">{t('detection.actionLabel')}</label>
                <Select value={action} onValueChange={(v) => setAction(v as Action)} disabled={apply.isPending}>
                  <SelectTrigger id="group-detection-action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIONS.map((a) => (
                      <SelectItem key={a} value={a}>{t(`detection.${a}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-4 min-h-[4.5rem] rounded-lg bg-muted/50 p-3 text-sm" aria-live="polite">
                {preview.isError ? (
                  <p className="text-destructive">{t('detection.previewFailed')}</p>
                ) : !counts || preview.isFetching ? (
                  <p className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />{t('detection.checking')}
                  </p>
                ) : (
                  <ul className="space-y-0.5">
                    <li className="font-medium">{t('detection.willChange', { count: counts.updated.length })}</li>
                    {counts.unchanged.length > 0 && (
                      <li className="text-muted-foreground">{t('detection.alreadySet', { count: counts.unchanged.length })}</li>
                    )}
                    {counts.skipped.length > 0 && (
                      <li className="text-muted-foreground">{t('detection.notYours', { count: counts.skipped.length })}</li>
                    )}
                  </ul>
                )}
              </div>
            </>
          )}

          <div className="mt-6 flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={apply.isPending}>
              {tc('actions.cancel')}
            </Button>
            <Button className="flex-1" onClick={() => apply.mutate()} disabled={!canApply}>
              {apply.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('detection.apply')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
